import PageSEO from "@/components/PageSEO";
import StickyHeader from "@/components/StickyHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import SiteFooter from "@/components/SiteFooter";

import { MapPin, Phone, ThermometerSun, Star, HelpCircle, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import areasHeroImg from "@/assets/areas-we-service-hero.jpg";

interface Suburb {
  name: string;
  slug: string;
  distance: string;
  description: string;
  hotPlace: string;
  hotPlaceDescription: string;
}

const suburbs: Suburb[] = [
  { name: "Bowral", slug: "bowral", distance: "8 km", description: "The heart of the Southern Highlands, Bowral blends grand heritage homes, cool-climate gardens and a thriving village centre. With winter mornings regularly dropping below zero, reverse-cycle systems here are sized on heating load first — and we specialise in retrofitting period homes without spoiling their character.", hotPlace: "Bradman Museum & International Cricket Hall of Fame", hotPlaceDescription: "Australia's home of cricket history, celebrating Sir Donald Bradman beside the picturesque Bradman Oval — a Bowral institution and a year-round drawcard." },
  { name: "Mittagong", slug: "mittagong", distance: "0 km", description: "Our home base. Mittagong's mix of older weatherboard cottages, brick homes and new estates sits at around 640m elevation, where frosts are the norm from May to September. We handle everything from ducted reverse-cycle installs to replacing tired wood heaters.", hotPlace: "Mount Alexandra Reserve", hotPlaceDescription: "A bushland reserve with walking tracks, waterfalls and lookouts over the Highlands — a favourite escape right on Mittagong's doorstep." },
  { name: "Moss Vale", slug: "moss-vale", distance: "13 km", description: "A working country town with rural surrounds, Moss Vale sees some of the coldest overnight temperatures in the Highlands. Larger homes and acreage properties here benefit from zoned ducted systems with strong low-ambient heating performance.", hotPlace: "Leighton Gardens", hotPlaceDescription: "A beautiful town-centre park bursting with tulips and cherry blossom in spring, and a blaze of colour through autumn." },
  { name: "Berrima", slug: "berrima", distance: "13 km", description: "A remarkably intact 1830s sandstone village where heritage requirements shape every installation. We design discreet systems that heat solid-masonry homes efficiently while keeping outdoor units out of sight.", hotPlace: "Berrima Courthouse", hotPlaceDescription: "A striking Georgian sandstone courthouse from 1838, now a museum telling the story of colonial justice in the Highlands." },
  { name: "Burradoo", slug: "burradoo", distance: "10 km", description: "Leafy Burradoo is known for large estates on generous, tree-lined blocks. Whole-home ducted reverse-cycle with multiple zones is the standard here, often paired with hydronic upgrades for the coldest wings of the house.", hotPlace: "Burradoo's heritage estates", hotPlaceDescription: "Grand gardens and rambling country residences along Kangaloon and Burradoo Roads — some of the most admired properties in the Highlands." },
  { name: "Bundanoon", slug: "bundanoon", distance: "27 km", description: "Perched on the edge of Morton National Park at roughly 700m, Bundanoon is one of the chilliest towns we service. Cottages here are often poorly insulated, so we pair the right system size with practical advice on sealing and zoning.", hotPlace: "Glow Worm Glen", hotPlaceDescription: "A short bush walk leading to a fern-lined grotto that lights up with glow worms after dark — magic on a clear, cold night." },
  { name: "Robertson", slug: "robertson", distance: "26 km", description: "The highest and wettest town in the district, Robertson gets fog, driving rain and bitter southerlies. Low-ambient heating capability isn't optional here — it's the whole point, and we spec systems accordingly.", hotPlace: "Robertson Pie Shop", hotPlaceDescription: "The famous roadside pie shop that has warmed travellers on the Illawarra Highway for decades — a genuine Highlands landmark." },
  { name: "Exeter", slug: "exeter", distance: "22 km", description: "A tiny village surrounded by farmland and horse properties. Rural blocks mean longer cable runs and careful outdoor unit siting, all of which we plan for before a single bracket goes up.", hotPlace: "Exeter General Store", hotPlaceDescription: "A beloved village store and cafe that doubles as the town's meeting place — coffee, provisions and local gossip in equal measure." },
  { name: "Sutton Forest", slug: "sutton-forest", distance: "20 km", description: "Historic estates, country guesthouses and grazing land define Sutton Forest. We regularly work on large heritage homes where discreet ducting and quiet operation matter as much as raw heating output.", hotPlace: "Hillview Estate", hotPlaceDescription: "The former vice-regal country residence set among rolling paddocks — a landmark of the district's colonial history." },
  { name: "Colo Vale", slug: "colo-vale", distance: "7 km", description: "A quiet, elevated village north of Mittagong with plenty of older homes and newer family builds. Frost pockets here get seriously cold, so we size for the coldest week of the year, not the average.", hotPlace: "Colo Vale Reserve", hotPlaceDescription: "A community sports and picnic reserve backing onto bushland, the centre of village life for local families." },
  { name: "Hill Top", slug: "hill-top", distance: "12 km", description: "One of the highest villages in the district, Hill Top sits among tall bushland and regularly records the district's lowest overnight temperatures. Reliable winter heating is the number one priority for every job here.", hotPlace: "Hill Top village green", hotPlaceDescription: "A peaceful bush-fringed village centre popular with walkers, cyclists and families escaping the coastal heat in summer." },
  { name: "Yerrinbool", slug: "yerrinbool", distance: "12 km", description: "A small rail village on the northern edge of the Highlands. Homes range from fibro weekenders to modern acreage builds — all of which need heating that copes with frosty mornings and mild afternoons.", hotPlace: "Yerrinbool Station", hotPlaceDescription: "A charming heritage rail stop on the Southern Highlands line, a reminder of the villages that grew up along the railway." },
  { name: "Welby", slug: "welby", distance: "2 km", description: "Right next door to our Mittagong base, Welby mixes light industrial premises with established homes. Same-day service is genuinely same-day here, whether it's a workshop warm-up or a home breakdown.", hotPlace: "Welby industrial precinct", hotPlaceDescription: "A busy local trade and light-industry hub servicing the wider Highlands — and one of our most frequent commercial call-out areas." },
  { name: "Wingello", slug: "wingello", distance: "34 km", description: "A forest village bordering Wingello State Forest, where bushfire-resilient siting and robust equipment matter. We install systems that keep working through cold snaps and long, damp winters.", hotPlace: "Wingello State Forest", hotPlaceDescription: "A vast pine and eucalypt forest laced with mountain bike trails and fire roads — a mecca for riders across the state." },
  { name: "Penrose", slug: "penrose", distance: "37 km", description: "A quiet rural community of acreage blocks and small farms. Off-the-beaten-track properties are no problem for our team, and we never charge extra travel within the Highlands.", hotPlace: "Penrose State Forest", hotPlaceDescription: "Peaceful pine plantation trails popular with walkers, riders and four-wheel drivers exploring the southern Highlands." },
  { name: "Goulburn", slug: "goulburn", distance: "60 km", description: "Australia's first inland city, Goulburn is famously cold and windy, with hard frosts and sub-zero winter mornings. Heavy-duty reverse-cycle heating and ducted systems are our bread and butter here.", hotPlace: "The Big Merino", hotPlaceDescription: "The 15-metre concrete ram that made Goulburn famous — an unmissable tribute to the region's wool industry." },
  { name: "Picton", slug: "picton", distance: "27 km", description: "Set in the Wollondilly valley, Picton swings between frosty winter mornings and hot summer afternoons. That spread makes properly sized reverse-cycle systems especially important for year-round comfort.", hotPlace: "Redbank Range Tunnel", hotPlaceDescription: "A hand-cut 1860s railway tunnel steeped in local legend, and one of Picton's most visited historic curiosities." },
  { name: "Tahmoor", slug: "tahmoor", distance: "31 km", description: "A growing Wollondilly township with plenty of family homes and new estates. Ducted reverse-cycle with zoning is the most popular choice for keeping the whole house warm without the bill shock.", hotPlace: "Tahmoor Gorge", hotPlaceDescription: "A dramatic sandstone gorge on the Bargo River with swimming holes and walking tracks just minutes from town." },
  { name: "Bargo", slug: "bargo", distance: "26 km", description: "Semi-rural Bargo is full of larger blocks and older homes with limited insulation. We focus on heating performance and running costs, often replacing slow, expensive wood or gas heating.", hotPlace: "Bargo River Reserve", hotPlaceDescription: "A rugged river reserve with rock pools and bushwalks, a favourite local spot for summer swims and winter walks." },
  { name: "Thirlmere", slug: "thirlmere", distance: "31 km", description: "A historic rail village bordering Thirlmere Lakes National Park. Cottages and character homes here need heating solutions that are efficient, quiet and sympathetic to the streetscape.", hotPlace: "NSW Rail Museum", hotPlaceDescription: "Australia's largest railway heritage collection, with beautifully restored steam locomotives and regular heritage train rides." },
];

const faqs = [
  {
    question: "What areas does ACG Southern Highlands service?",
    answer: "We service the entire Wingecarribee Shire and surrounds from our Mittagong base — including Bowral, Moss Vale, Berrima, Burradoo, Bundanoon, Robertson, Exeter, Sutton Forest, Colo Vale, Hill Top, Yerrinbool, Welby, Wingello and Penrose — plus Goulburn to the south and the Wollondilly towns of Picton, Tahmoor, Bargo and Thirlmere to the north."
  },
  {
    question: "How quickly can you attend a heating or air conditioning breakdown?",
    answer: "Most Highlands towns receive same-day or next-day attendance. During winter cold snaps we prioritise homes with no heating at all — particularly households with young children or elderly residents — and aim to be on site within a few hours."
  },
  {
    question: "Do you charge extra to travel to outlying towns?",
    answer: "No. Whether you're in Mittagong, Bundanoon, Goulburn or Thirlmere, our pricing is the same. Travel within our service area is included in every quote, with no hidden call-out surcharges."
  },
  {
    question: "What type of system suits the Southern Highlands climate?",
    answer: "At 600-750m elevation we get heavy frosts, sub-zero mornings and only occasional summer heatwaves — so systems must be sized on winter heating load, not cooling. We specify inverter reverse-cycle units rated for strong low-ambient heating output, typically ducted with zoning for whole homes, or high-capacity splits for individual living areas."
  },
  {
    question: "Are your technicians licensed to work in NSW?",
    answer: "Yes. Our team holds current NSW electrical licences and ARC authorisation for refrigerant handling, and we're fully insured. All work complies with NSW regulations and Australian Standards, and we're trained across Daikin, Fujitsu, Mitsubishi Heavy Industries, ActronAir, Samsung, LG, Rinnai and Braemar equipment."
  },
  {
    question: "Can you service my older unit, or do I need a new one?",
    answer: "We service and repair all major brands regardless of age. That said, older systems often struggle to produce heat on frosty Highlands mornings — if yours is 10-15 years old and losing capacity in winter, we'll give you an honest comparison of repair versus replacement. No pressure either way."
  },
];

const AreasWeService = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen">
      <PageSEO title="Areas We Service | Southern Highlands NSW | ACG" description="ACG Southern Highlands services 20 towns from Mittagong to Goulburn. Find your town for expert heating and air conditioning installation, repairs and service." />
      <StickyHeader />
      <Breadcrumbs items={[{ label: "Areas We Service" }]} />

      {/* Hero Section */}
      <section className="relative h-[400px] md:h-[500px] overflow-hidden">
        <img
          src={areasHeroImg}
          alt="Heating and air conditioning service areas across the Southern Highlands, NSW"
          className="w-full h-full object-cover"
          width={1920}
          height={768}
        />
        <div className="absolute inset-0 bg-acg-dark/70 flex items-center justify-center">
          <div className="text-center text-primary-foreground px-4">
            <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tight mb-4">
              Areas We Service
            </h1>
            <p className="text-lg md:text-xl max-w-3xl mx-auto opacity-90">
              Professional heating and air conditioning installation, repair &amp; maintenance across the Southern Highlands, Wollondilly and Goulburn — from our base in Mittagong.
            </p>
          </div>
        </div>
      </section>

      {/* Article Introduction — E-E-A-T Content */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4 max-w-4xl">
          <article className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-foreground mb-6">
              The Southern Highlands' Trusted Heating &amp; Air Conditioning Experts — Servicing 20 Towns
            </h2>

            <p className="text-muted-foreground leading-relaxed">
              At ACG Southern Highlands, we're a local heating and air conditioning team based in Mittagong,
              delivering expert installation, repair and maintenance to homes and businesses right across the
              Wingecarribee Shire, the Wollondilly and down to Goulburn. As authorised dealers for Daikin,
              Fujitsu, Mitsubishi Heavy Industries, ActronAir and more, we bring factory-trained technicians,
              NSW electrical licensing and ARC refrigerant authorisation to every job — no matter the town.
            </p>

            <h3 className="text-2xl font-bold text-foreground mt-10 mb-4">
              Why the Southern Highlands Needs Heating-First Climate Control
            </h3>
            <p className="text-muted-foreground leading-relaxed">
              Sitting between roughly 600 and 750 metres above sea level, the Southern Highlands is one of the
              coldest inhabited areas in coastal New South Wales. Winter brings heavy frosts, fog and overnight
              temperatures that regularly fall below zero, while summers stay comparatively mild with only the
              occasional run of 35°C-plus days. That climate profile flips the usual approach on its head: here,
              a system must be sized on its winter heating load, not its cooling capacity.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Conditions still vary town to town. Robertson and Bundanoon sit higher and wetter, with fog and
              bitter southerlies. Frost pockets around Colo Vale, Hill Top and Moss Vale can be several degrees
              colder than nearby Bowral on the same morning. Goulburn adds wind and long, dry cold snaps, while
              the Wollondilly towns of Picton, Tahmoor and Bargo swing further into summer heat. A one-size-fits-all
              quote simply doesn't work — which is why local knowledge matters.
            </p>

            <h3 className="text-2xl font-bold text-foreground mt-10 mb-4">
              Our Service Area — Mittagong to Goulburn, Bundanoon to Thirlmere
            </h3>
            <p className="text-muted-foreground leading-relaxed">
              We cover 20 towns and villages across the Southern Highlands and surrounds: Bowral, Mittagong,
              Moss Vale, Berrima, Burradoo, Bundanoon, Robertson, Exeter, Sutton Forest, Colo Vale, Hill Top,
              Yerrinbool, Welby, Wingello, Penrose, Goulburn, Picton, Tahmoor, Bargo and Thirlmere. Whether you're
              in a heritage sandstone cottage in Berrima, a rambling estate in Burradoo, a rural property outside
              Exeter or a new build in Tahmoor, you get the same standard of workmanship — and no travel surcharge.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Our service vehicles carry a comprehensive range of parts and tools so most repairs are completed on
              the first visit. For installations, we conduct a proper on-site assessment — measuring rooms, checking
              insulation, ceiling space, orientation and existing switchboard capacity — before recommending a
              system and giving you a fixed, itemised quote.
            </p>

            <h3 className="text-2xl font-bold text-foreground mt-10 mb-4">
              What Sets ACG Southern Highlands Apart
            </h3>
            <div className="grid md:grid-cols-2 gap-6 my-8">
              {[
                { icon: "🏆", title: "Authorised Dealer Network", text: "Official partnerships with Daikin, Fujitsu, Mitsubishi Heavy Industries, ActronAir and more mean genuine parts and factory-backed warranties.", gradient: "from-amber-500/10 to-orange-500/10", border: "border-amber-500/20", iconBg: "bg-amber-500/15" },
                { icon: "❄️", title: "Cold-Climate Specialists", text: "We size every system on winter heating load and specify units rated for strong output on sub-zero Highlands mornings.", gradient: "from-blue-500/10 to-cyan-500/10", border: "border-blue-500/20", iconBg: "bg-blue-500/15" },
                { icon: "💰", title: "Interest-Free Finance", text: "Flexible payment plans with up to 60 months interest-free through Zip Money, making comfort affordable for every household.", gradient: "from-emerald-500/10 to-teal-500/10", border: "border-emerald-500/20", iconBg: "bg-emerald-500/15" },
                { icon: "🛡️", title: "Licensed & ARC Authorised", text: "NSW electrical licensing, ARC refrigerant authorisation and full insurance on every job, big or small.", gradient: "from-violet-500/10 to-purple-500/10", border: "border-violet-500/20", iconBg: "bg-violet-500/15" },
              ].map((item) => (
                <div key={item.title} className={`bg-gradient-to-br ${item.gradient} rounded-2xl p-7 border ${item.border} hover:shadow-lg hover:scale-[1.02] transition-all duration-300 group`}>
                  <div className={`w-14 h-14 ${item.iconBg} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <span className="text-2xl">{item.icon}</span>
                  </div>
                  <h4 className="font-bold text-lg text-foreground mb-2">{item.title}</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-bold text-foreground mt-10 mb-4">
              Choosing the Right System for Your Highlands Home
            </h3>
            <p className="text-muted-foreground leading-relaxed">
              In period homes around Bowral, Berrima and Moss Vale, high-capacity reverse-cycle splits are often the
              most practical option — minimal structural work, no damage to heritage detailing, and quiet, efficient
              heating in the rooms you actually live in. Modern inverter units from Daikin, Fujitsu and Mitsubishi
              Heavy Industries hold their heating output well below zero and cost far less to run than portable or
              radiant electric heating.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              For larger homes and acreage properties around Burradoo, Sutton Forest, Exeter and Goulburn, ducted
              reverse-cycle with zoning delivers whole-home warmth on frosty mornings while letting you shut down
              unused wings. Proper zoning typically cuts running costs substantially compared with heating the whole
              house at once — and it pairs well with replacing an ageing wood heater or expensive gas system.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              For commercial premises in Mittagong, Bowral, Moss Vale and Goulburn — shops, cafes, clinics, offices
              and workshops — we supply cassette units, multi-head systems and VRV/VRF solutions, including
              after-hours installation so your trading isn't disrupted.
            </p>

            <h3 className="text-2xl font-bold text-foreground mt-10 mb-4">
              Our Process — From Quote to Installation
            </h3>
            <p className="text-muted-foreground leading-relaxed">
              Getting heating and cooling installed shouldn't be stressful. Contact us for a free, no-obligation
              quote — we'll visit your property, assess your heating load and provide a transparent, itemised price
              with no hidden costs. Once you approve, we schedule installation at a time that suits you, often within
              the same week. Our technicians arrive on time, install to Australian Standards and thoroughly
              commission the system before handover, then walk you through the controls, timers and zoning so you
              get the most from it. Afterwards, our maintenance plans keep the system performing through every winter.
            </p>
          </article>
        </div>
      </section>

      {/* Suburbs Grid */}
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black uppercase text-foreground mb-4">
              Suburbs We Service
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Explore the towns across the Southern Highlands, Wollondilly and Goulburn where our technicians provide heating and air conditioning services. Click any town to learn more.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {suburbs.map((suburb) => (
              <Link
                to={`/areas-we-service/${suburb.slug}`}
                key={suburb.name}
                className="bg-card rounded-xl shadow-md hover:shadow-xl transition-shadow overflow-hidden group border border-border"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-bold text-card-foreground group-hover:text-primary transition-colors">
                      {suburb.name}
                    </h3>
                    <span className="flex items-center gap-1 text-xs font-semibold text-secondary bg-secondary/10 px-2 py-1 rounded-full whitespace-nowrap">
                      <MapPin className="w-3 h-3" />
                      {suburb.distance}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                    {suburb.description}
                  </p>
                  <div className="bg-muted rounded-lg p-4 border border-border/50">
                    <div className="flex items-center gap-2 mb-2">
                      <Star className="w-4 h-4 text-secondary" />
                      <span className="text-sm font-bold text-foreground">{suburb.hotPlace}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {suburb.hotPlaceDescription}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black uppercase text-foreground mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-muted-foreground">
              Everything you need to know about our heating and air conditioning services across the Southern Highlands.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-card rounded-xl overflow-hidden border border-border shadow-sm"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <HelpCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span className="font-semibold text-card-foreground">{faq.question}</span>
                  </div>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6 pt-0 bg-muted/30">
                    <p className="text-muted-foreground leading-relaxed pl-8">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4 text-center">
          <ThermometerSun className="w-12 h-12 text-primary-foreground mx-auto mb-4" />
          <h2 className="text-3xl md:text-4xl font-black text-primary-foreground uppercase mb-4">
            Need Heating or Air Conditioning in Your Town?
          </h2>
          <p className="text-primary-foreground/90 max-w-2xl mx-auto mb-8 text-lg">
            Get a free, no-obligation quote from the Southern Highlands' trusted heating and air conditioning team. Same-day service available across our whole service area.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:0480841485"
              className="flex items-center gap-2 bg-primary-foreground text-primary font-bold px-8 py-4 rounded-lg text-lg hover:opacity-90 transition-opacity"
            >
              <Phone className="w-5 h-5" />
              Call 0480 841 485
            </a>
            <a
              href="#quote"
              className="flex items-center gap-2 bg-transparent border-2 border-primary-foreground text-primary-foreground font-bold px-8 py-4 rounded-lg text-lg hover:bg-primary-foreground/10 transition-colors"
            >
              Get a Free Quote
            </a>
          </div>
        </div>
      </section>

      {/* JSON-LD Structured Data for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Heating & Air Conditioning Installation, Repair & Maintenance",
            "provider": {
              "@type": "LocalBusiness",
              "name": "ACG Southern Highlands",
              "telephone": "0480 841 485",
              "areaServed": suburbs.map((s) => ({
                "@type": "City",
                "name": s.name + ", NSW",
              })),
            },
            "serviceType": "Air Conditioning Services",
            "areaServed": {
              "@type": "GeoCircle",
              "geoMidpoint": {
                "@type": "GeoCoordinates",
                "latitude": -34.4500,
                "longitude": 150.4500,
              },
              "geoRadius": "60000",
            },
          }),
        }}
      />

      {/* FAQ Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map((faq) => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer,
              },
            })),
          }),
        }}
      />

      <SiteFooter />
    </div>
  );
};

export default AreasWeService;
